/**
 * ReportSubscriptionsPage — manage automated report deliveries (#183).
 *
 * Lists the org's report subscriptions with their cadence, recipients, and last
 * send. Owner/admin can toggle, send-now, and delete. New subscriptions are
 * created from a saved report via the "Schedule delivery" button on the builder.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Card, Button, Badge, Spinner, EmptyState, DeleteConfirmDialog } from '@/components/ui';
import { Switch } from '@/components/ui/Switch';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import {
  listReportSubscriptions,
  updateReportSubscription,
  deleteReportSubscription,
  testSendReportSubscription,
} from '@/services/reportSubscriptionService';
import type { ReportSubscription } from '@simcheck/shared-types';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function cadenceSummary(s: ReportSubscription): string {
  if (s.trigger === 'on_completion') {
    const what = s.testConfigurationName ? `“${s.testConfigurationName}”` : 'a test';
    const when = s.deliverOn === 'on_failure' ? ' — only on failure' : '';
    return `When ${what} completes${when}`;
  }
  const c = s.cadence;
  if (!c) return 'Scheduled';
  const time = `${String(c.hour).padStart(2, '0')}:${String(c.minute ?? 0).padStart(2, '0')}`;
  const base =
    c.frequency === 'daily' ? `Daily at ${time}`
    : c.frequency === 'weekly' ? `Weekly on ${DOW[c.dayOfWeek ?? 1]} at ${time}`
    : `Monthly on day ${c.dayOfMonth ?? 1} at ${time}`;
  return `${base} (${c.timezone})`;
}

function windowLabel(w?: ReportSubscription['window']): string {
  switch (w) {
    case 'last_24h': return 'last 24h';
    case 'last_7d': return 'last 7d';
    case 'last_30d': return 'last 30d';
    case 'last_month': return 'last month';
    default: return '—';
  }
}

export default function ReportSubscriptionsPage() {
  const { activeOrganization } = useAuth();
  const fmt = useDateFormat();
  const navigate = useNavigate();
  const toast = useToast();
  const [subs, setSubs] = useState<ReportSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Subscription waiting for confirmation in the DeleteConfirmDialog
  const [pendingDelete, setPendingDelete] = useState<ReportSubscription | null>(null);

  const load = useCallback(async () => {
    if (!activeOrganization?.id) return;
    setLoading(true);
    try {
      setSubs(await listReportSubscriptions(activeOrganization.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, toast]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (s: ReportSubscription) => {
    if (!activeOrganization?.id) return;
    setBusyId(s.id);
    try {
      const updated = await updateReportSubscription(activeOrganization.id, s.id, { enabled: !s.enabled });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSendNow = async (s: ReportSubscription) => {
    if (!activeOrganization?.id) return;
    setBusyId(s.id);
    try {
      const r = await testSendReportSubscription(activeOrganization.id, s.id);
      if (r.status === 'sent') toast.success(`Sent to ${r.sentTo?.length ?? 0} recipient(s)`);
      else if (r.status === 'skipped_no_recipients') toast.error('No active member recipients to send to');
      else toast.error(`Delivery ${r.status}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (s: ReportSubscription) => {
    if (!activeOrganization?.id) return;
    setBusyId(s.id);
    try {
      await deleteReportSubscription(activeOrganization.id, s.id);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      toast.success('Subscription deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports/builder')} icon={<ChevronLeft className="w-4 h-4" />}>
            Back to report builder
          </Button>
          <h1 className="text-display font-semibold text-foreground mt-2 mb-1">Report delivery</h1>
          <p className="text-body text-muted-foreground">
            Scheduled email deliveries of your saved reports. Create one from the “Schedule delivery” button on a saved report.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : subs.length === 0 ? (
          <Card padding="lg">
            <EmptyState
              title="No scheduled deliveries"
              description="Open a saved report in the builder and click “Schedule delivery” to have it emailed on a cadence."
            />
          </Card>
        ) : (
          <Card padding="none">
            <ResponsiveTable
              data={subs}
              keyExtractor={(s) => s.id}
              columns={[
                {
                  key: 'name',
                  header: 'Report',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="font-medium">{s.name}</span>
                  ),
                },
                {
                  key: 'schedule',
                  header: 'Schedule',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="text-sm">{cadenceSummary(s)}</span>
                  ),
                },
                {
                  key: 'covering',
                  header: 'Covering',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="text-sm text-muted-foreground">{windowLabel(s.window)}</span>
                  ),
                },
                {
                  key: 'formats',
                  header: 'Formats',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="text-sm">{[s.format?.csv && 'CSV', s.format?.pdf && 'PDF'].filter(Boolean).join(' · ') || '—'}</span>
                  ),
                },
                {
                  key: 'recipients',
                  header: 'Recipients',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="text-sm tabular-nums">{s.recipients?.length ?? 0}</span>
                  ),
                },
                {
                  key: 'lastSent',
                  header: 'Last sent',
                  render: (_: unknown, s: ReportSubscription) => (
                    <span className="text-sm text-muted-foreground">
                      {s.lastSentAt ? fmt.formatDateTime(s.lastSentAt as any) : 'Never'}
                    </span>
                  ),
                },
                {
                  key: 'enabled',
                  header: 'Active',
                  render: (_: unknown, s: ReportSubscription) => (
                    <div className="flex items-center gap-2">
                      <Switch checked={s.enabled} disabled={busyId === s.id} onCheckedChange={() => handleToggle(s)} />
                      {s.lastError ? <Badge variant="destructive">error</Badge> : null}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (_: unknown, s: ReportSubscription) => (
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="sm" disabled={busyId === s.id} onClick={() => handleSendNow(s)} icon={<Send className="w-4 h-4" />}>
                        Send now
                      </Button>
                      <Button variant="ghost" size="sm" disabled={busyId === s.id} onClick={() => setPendingDelete(s)} icon={<Trash2 className="w-4 h-4" />} />
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </div>

      <DeleteConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        itemName={pendingDelete?.name}
        details="Its scheduled deliveries stop."
        onConfirm={async () => {
          if (pendingDelete) await handleDelete(pendingDelete);
        }}
      />
    </AppShell>
  );
}
